import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Plus, Trash2, ChevronUp, ChevronDown, Upload, X, Lightbulb, Image, Signal, Filter, Mic,
  Video, Type, Play, Youtube, Award, BookOpen, Code, Tag, Layers, Wand2, Eye, RefreshCw,
  CheckCircle, ArrowUpDown
} from 'lucide-react';
import QuestionCategoryForm from './QuestionCategoryForm';
import CategoryFilter from './CategoryFilter';
import { leitnerService } from '../../services/leitnerService';
import InlineMathField from '../math/InlineMathField';
import { MathRenderer } from '../math/MathRenderer';
import { VoiceRecorder } from '../shared/VoiceRecorder';
import { VoicePlayer } from '../shared/VoicePlayer';
import QuestionMediaSection from './QuestionMediaSection';
import QuestionMediaDisplay from './QuestionMediaDisplay';

import { GraphSketchCompact } from './GraphSketchEmbed';
import {
  DynamicQuestionInstanceBuilder,
  DEFAULT_TEMPLATES
} from '../../services/dynamicQuestionInstanceBuilder';
import {
  StatementCategory,
  StatementType,
  getTemplatesForFunction
} from '../../services/statementTemplates';

import {
  RESPONSE_TYPES,
  ResponseTypeSelector,
  ResponseInput,
  MultiselectInput,
  ShortAnswerInput,
  ParagraphInput,
  MatchingInput,
  OrderingInput
} from './ResponseTypeInputs';

import {
  DifficultySelector,
  HintSection,
  MarksInput,
  KnowledgeInput,
  SubtopicsInput,
  MultiGradeSelector,
  DIFFICULTY_LEVELS
} from './CreatorComponents';
import SimpleQuestionForm from './SimpleQuestionForm';



const FUNCTION_TYPES = [
  { id: 'linear', label: 'Linear', formula: 'y = mx + c', icon: '📈' },
  { id: 'quadratic', label: 'Quadratic', formula: 'y = a(x - p)² + q', icon: '📉' },
  { id: 'hyperbola', label: 'Hyperbola', formula: 'y = a/(x + p) + q', icon: '〰️' },
  { id: 'exponential', label: 'Exponential', formula: 'y = a·bˣ⁺ᵖ + q', icon: '📊' }
];

const CATEGORY_LABELS = {
  [StatementCategory.STRUCTURAL]: { label: 'Structural', color: 'bg-purple-500' },
  [StatementCategory.ORIENTATION]: { label: 'Orientation', color: 'bg-blue-500' },
  [StatementCategory.INTERCEPTS]: { label: 'Intercepts', color: 'bg-green-500' },
  [StatementCategory.DOMAIN_RANGE]: { label: 'Domain & Range', color: 'bg-yellow-500' },
  [StatementCategory.INTERVALS]: { label: 'Intervals', color: 'bg-orange-500' },
  [StatementCategory.TRANSFORMATIONS]: { label: 'Transformations', color: 'bg-pink-500' },
  [StatementCategory.ASYMPTOTES]: { label: 'Asymptotes', color: 'bg-red-500' },
  [StatementCategory.BEHAVIOUR]: { label: 'Behaviour', color: 'bg-teal-500' },
  [StatementCategory.INVERSES]: { label: 'Inverses', color: 'bg-indigo-500' }
};

function getDefaultParamConfig(type) {
  switch (type) {
    case 'linear':
      return { m: { values: [-3, -2, -1, 1, 2, 3], exclude: [0] }, c: { min: -10, max: 10, step: 1 } };
    case 'quadratic':
      return { a: { values: [-3, -2, -1, 1, 2, 3], exclude: [0] }, p: { min: -5, max: 5, step: 1 }, q: { min: -6, max: 6, step: 1 } };
    case 'hyperbola':
      return { a: { values: [-4, -3, -2, -1, 1, 2, 3, 4], exclude: [0] }, p: { min: -4, max: 4, step: 1 }, q: { min: -4, max: 4, step: 1 } };
    case 'exponential':
      return { a: { values: [-2, -1, 1, 2], exclude: [0] }, b: { values: [0.5, 2, 3] }, p: { min: -3, max: 3, step: 1 }, q: { min: -4, max: 4, step: 1 } };
    default:
      return {};
  }
}

const CreatorView = ({ bookId, addQuestion, questions, deleteQuestion, updateQuestion }) => {
  const [createType, setCreateType] = useState('simple');
  const [error, setError] = useState(null);


  // Stem question state - shared stem only (metadata moved to subquestions)
  const [stemText, setStemText] = useState('');
  const [stemMedia, setStemMedia] = useState({ type: 'none', images: [], video: null, embed: null });
  const [subQuestions, setSubQuestions] = useState([
    {
      text: '',
      responseType: RESPONSE_TYPES.MULTISELECT,
      responseData: {
        options: [{ id: 'opt_1', text: '' }, { id: 'opt_2', text: '' }],
        correctOptionIds: []
      },
      level: 1,
      marks: 1,
      hint: { text: '', audio: null, video: null },
      requiredKnowledge: { skills: [], concepts: [] },
      subtopics: []
    }
  ]);

  const [editingQuestion, setEditingQuestion] = useState(null);
  const [categories, setCategories] = useState({ subjects: [], grades: [], topics: [], subtopics: [] });
  // Session-added categories (persists across form resets until page reload)
  const [sessionCategories, setSessionCategories] = useState({ subjects: [], grades: [], topics: [], subtopics: [] });
  // Updated to support multiple grades
  const [questionCategories, setQuestionCategories] = useState({
    subject: null,
    grades: [], // Changed from grade to grades (array)
    topics: [],
    subtopics: []
  });
  const [filters, setFilters] = useState({ subject: null, grades: [], topics: [] });
  const [filteredQuestions, setFilteredQuestions] = useState([]);

  const [questionStatus, setQuestionStatus] = useState('draft');

  const [dynamicFunctionType, setDynamicFunctionType] = useState('quadratic');
  const [dynamicParamConfig, setDynamicParamConfig] = useState(getDefaultParamConfig('quadratic'));
  const [dynamicStatementConfig, setDynamicStatementConfig] = useState({
    includeCategories: Object.values(StatementCategory),
    totalOptions: { min: 5, max: 6 },
    correctOptions: { min: 2, max: 4 },
    incorrectOptions: { min: 2, max: 3 },
    requiredCategories: [StatementCategory.STRUCTURAL],
    targetDifficulty: { min: 1, max: 3 }
  });
  const [dynamicMarks, setDynamicMarks] = useState(4);
  const [dynamicPreview, setDynamicPreview] = useState(null);
  const [dynamicPreviewSeed, setDynamicPreviewSeed] = useState(Date.now());
  const [dynamicCustomStatements, setDynamicCustomStatements] = useState([]);
  const [dynamicStatementSource, setDynamicStatementSource] = useState('merged');
  const [dynamicTemplates, setDynamicTemplates] = useState([]);
  const [selectedDynamicTemplateId, setSelectedDynamicTemplateId] = useState('');
  const [dynamicTemplateName, setDynamicTemplateName] = useState('');
  const [dynamicTemplateError, setDynamicTemplateError] = useState(null);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [showDynamicHelper, setShowDynamicHelper] = useState(false);
  const [simpleFormKey, setSimpleFormKey] = useState(0);

  // Merged categories: DB categories + session-added categories
  const mergedCategories = useMemo(() => ({
    subjects: [...new Set([...(categories.subjects || []), ...sessionCategories.subjects])].sort(),
    grades: [...new Set([...(categories.grades || []), ...sessionCategories.grades])].sort(),
    topics: [...new Set([...(categories.topics || []), ...sessionCategories.topics])].sort(),
    subtopics: [...new Set([...(categories.subtopics || []), ...sessionCategories.subtopics])].sort()
  }), [categories, sessionCategories]);


  const resetStemForm = () => {
    setStemText('');
    setStemMedia({ type: 'none', images: [], video: null, embed: null });
    setSubQuestions([{
      text: '',
      responseType: RESPONSE_TYPES.MULTISELECT,
      responseData: {
        options: [{ id: 'opt_1', text: '' }, { id: 'opt_2', text: '' }],
        correctOptionIds: []
      },
      level: 1,
      marks: 1,
      hint: { text: '', audio: null, video: null },
      requiredKnowledge: { skills: [], concepts: [] },
      subtopics: []
    }]);
    setQuestionCategories({ subject: null, grades: [], topics: [], subtopics: [] });
    setQuestionStatus('draft');
    setError(null);
  };

  const resetSimpleForm = () => {
    setSimpleFormKey(prev => prev + 1);
  };

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await leitnerService.getQuestionCategories(bookId);
        setCategories({
          ...cats,
          subtopics: cats.subtopics || []
        });
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
    };

    if (bookId) {
      loadCategories();
    }
  }, [bookId]);

  useEffect(() => {
    const loadDynamicTemplates = async () => {
      try {
        const templates = await leitnerService.getDynamicTemplates(bookId);
        setDynamicTemplates(templates);
      } catch (error) {
        console.error('Failed to load dynamic templates:', error);
      }
    };

    if (bookId) {
      loadDynamicTemplates();
    }
  }, [bookId]);

  useEffect(() => {
    const filtered = filterQuestionsByCategories(questions, filters);
    setFilteredQuestions(filtered);
  }, [questions, filters]);

  // Updated filter function to handle multiple grades
  const filterQuestionsByCategories = (questions, filters) => {
    if (!filters.subject && (!filters.grades || filters.grades.length === 0) && (!filters.topics || filters.topics.length === 0)) {
      return questions;
    }

    return questions.filter(q => {
      let matches = true;

      if (filters.subject && q.subject !== filters.subject) {
        matches = false;
      }

      // Handle both old single grade and new multi-grade format
      if (filters.grades && filters.grades.length > 0) {
        const questionGrades = Array.isArray(q.grades) ? q.grades : (q.grade ? [q.grade] : []);
        const hasMatchingGrade = filters.grades.some(g => questionGrades.includes(g));
        if (!hasMatchingGrade) {
          matches = false;
        }
      }

      if (filters.topics && filters.topics.length > 0) {
        const questionTopics = q.topics || [];
        const hasMatchingTopic = filters.topics.some(topic => questionTopics.includes(topic));
        if (!hasMatchingTopic) {
          matches = false;
        }
      }

      return matches;
    });
  };

  const handleQuickCreateQuestion = async (...args) => {
    const data = args[0];
    try {
      // Handle potential legacy calls if any remain
      if (typeof data === 'string') {
        const [text, options, correctIds] = args;
        const legacyData = {
          type: 'simple',
          responseType: RESPONSE_TYPES.MULTISELECT,
          status: 'published',
          subject: questionCategories.subject,
          grades: questionCategories.grades,
          topics: questionCategories.topics,
          subtopics: questionCategories.subtopics,
          text: text.trim(),
          options: options,
          correctOptionIds: correctIds,
          level: 1,
          marks: 1,
          autoGrade: true
        };
        return await addQuestion(legacyData);
      }

      // data is the full object from SimpleQuestionForm
      const fullData = {
        ...data,
        subject: questionCategories.subject,
        grades: questionCategories.grades,
        topics: questionCategories.topics,
        subtopics: questionCategories.subtopics,
        ...buildResponseTypeData(data.responseType, data.responseData)
      };
      delete fullData.responseData;

      const result = await addQuestion(fullData);
      return result;
    } catch (err) {
      console.error('Quick create failed:', err);
      setError('Failed to create branch question');
      return null;
    }
  };

  const handleStemSubmit = async (e) => {
    e.preventDefault();

    if (!stemText.trim()) {
      setError('Stem text is required');
      return;
    }

    const validSubQuestions = subQuestions.filter(sq => sq.text.trim());
    if (validSubQuestions.length === 0) {
      setError('At least one sub-question is required');
      return;
    }

    for (let i = 0; i < validSubQuestions.length; i++) {
      const sq = validSubQuestions[i];
      const respType = sq.responseType || RESPONSE_TYPES.MULTISELECT;

      if (respType === RESPONSE_TYPES.MULTISELECT) {
        const filledOptions = (sq.options || []).filter(opt => opt.text.trim());
        if (filledOptions.length < 2) {
          setError(`Sub-question ${i + 1} needs at least 2 options`);
          return;
        }
        if ((sq.correctOptionIds || []).length === 0) {
          setError(`Sub-question ${i + 1} needs at least one correct answer`);
          return;
        }
      } else if (respType === RESPONSE_TYPES.SHORT_ANSWER) {
        if (!sq.correctAnswer?.trim()) {
          setError(`Sub-question ${i + 1} needs a correct answer`);
          return;
        }
      } else if (respType === RESPONSE_TYPES.MATCHING) {
        const validPairs = (sq.matchingPairs || []).filter(p => p.prompt?.trim() && p.match?.trim());
        if (validPairs.length < 2) {
          setError(`Sub-question ${i + 1} needs at least 2 matching pairs`);
          return;
        }
      } else if (respType === RESPONSE_TYPES.ORDERING) {
        const validItems = (sq.orderingItems || []).filter(item => item.text?.trim());
        if (validItems.length < 2) {
          setError(`Sub-question ${i + 1} needs at least 2 ordering items`);
          return;
        }
      } else if (respType === RESPONSE_TYPES.CALCULATION) {
        const validSteps = (sq.solutionSteps || []).filter(s => s.text?.trim());
        if (validSteps.length < 1) {
          setError(`Sub-question ${i + 1} needs at least 1 solution step`);
          return;
        }
      }
      // PARAGRAPH type has no required answer fields
    }

    try {
      const processedSubQuestions = validSubQuestions.map(sq => {
        const hasHint = sq.hint?.text?.trim() || sq.hint?.audio || sq.hint?.video;
        const respType = sq.responseType || RESPONSE_TYPES.MULTISELECT;

        const baseData = {
          id: `sq_${Date.now()}_${Math.random()}`,
          text: sq.text.trim(),
          responseType: respType,
          level: sq.level || 1,
          marks: sq.marks || 1,
          hint: hasHint ? {
            text: sq.hint.text?.trim() || null,
            audio: sq.hint.audio || null,
            video: sq.hint.video || null
          } : null,
          requiredKnowledge: {
            skills: sq.requiredKnowledge?.skills?.filter(s => s.trim()) || [],
            concepts: sq.requiredKnowledge?.concepts?.filter(c => c.trim()) || []
          },
          subtopics: sq.subtopics || []
        };

        // Add response-type specific data
        if (respType === RESPONSE_TYPES.MULTISELECT) {
          baseData.options = (sq.options || []).filter(opt => opt.text.trim());
          baseData.correctOptionIds = sq.correctOptionIds || [];
        } else if (respType === RESPONSE_TYPES.SHORT_ANSWER) {
          baseData.correctAnswer = sq.correctAnswer?.trim() || '';
          baseData.acceptableAnswers = (sq.acceptableAnswers || []).filter(a => a.trim());
          baseData.caseSensitive = sq.caseSensitive || false;
        } else if (respType === RESPONSE_TYPES.CALCULATION) {
          baseData.solutionSteps = (sq.solutionSteps || []).filter(s => s.text?.trim());
          baseData.finalAnswer = sq.finalAnswer?.trim() || '';
        } else if (respType === RESPONSE_TYPES.PARAGRAPH) {
          baseData.rubric = sq.rubric || '';
          baseData.minWords = sq.minWords || 0;
          baseData.maxWords = sq.maxWords || 0;
        } else if (respType === RESPONSE_TYPES.MATCHING) {
          baseData.matchingPairs = (sq.matchingPairs || []).filter(p => p.prompt?.trim() && p.match?.trim());
        } else if (respType === RESPONSE_TYPES.ORDERING) {
          baseData.orderingItems = (sq.orderingItems || []).filter(i => i.text?.trim());
          baseData.correctOrder = baseData.orderingItems.map(i => i.id);
        }

        return baseData;
      });

      const questionData = {
        type: 'stem',
        status: questionStatus,
        subject: questionCategories.subject,
        grades: questionCategories.grades,
        topics: questionCategories.topics,
        stemText: stemText.trim(),
        media: stemMedia,
        subQuestions: subQuestions
          .filter(sq => sq.text.trim())
          .map((sq, idx) => ({
            id: sq.id || `sq_${Date.now()}_${idx}`,
            text: sq.text.trim(),
            responseType: sq.responseType || RESPONSE_TYPES.MULTISELECT,
            ...buildResponseTypeData(sq.responseType || RESPONSE_TYPES.MULTISELECT, sq.responseData || {
              options: sq.options,
              correctOptionIds: sq.correctOptionIds
            }),
            level: sq.level,
            marks: sq.marks,
            hint: (sq.hint?.text?.trim() || sq.hint?.audio || sq.hint?.video) ? sq.hint : null,
            requiredKnowledge: sq.requiredKnowledge,
            subtopics: sq.subtopics || [],
            autoGrade: (sq.responseType || RESPONSE_TYPES.MULTISELECT) !== RESPONSE_TYPES.PARAGRAPH
          }))
      };

      if (editingQuestion) {
        await updateQuestion(editingQuestion.id, questionData);
        setEditingQuestion(null);
      } else {
        await addQuestion(questionData);
      }

      resetStemForm();
    } catch (err) {
      setError('Failed to save question');
      console.error(err);
    }
  };

  const buildResponseTypeData = (responseType, responseData) => {
    switch (responseType) {
      case RESPONSE_TYPES.MULTISELECT:
        return {
          options: responseData.options?.filter(o => o.text.trim()) || [],
          correctOptionIds: responseData.correctOptionIds || []
        };
      case RESPONSE_TYPES.SHORT_ANSWER:
        return {
          correctAnswer: responseData.correctAnswer?.trim() || '',
          acceptableAnswers: responseData.acceptableAnswers?.filter(a => a.trim()) || [],
          caseSensitive: responseData.caseSensitive || false
        };
      case RESPONSE_TYPES.PARAGRAPH:
        return {
          rubric: responseData.rubric || '',
          minWords: responseData.minWords || 0,
          maxWords: responseData.maxWords || 0
        };
      case RESPONSE_TYPES.MATCHING:
        return {
          matchingPairs: responseData.matchingPairs?.filter(p => p.prompt.trim() && p.match.trim()) || []
        };
      case RESPONSE_TYPES.ORDERING:
        return {
          orderingItems: responseData.orderingItems?.filter(i => i.text.trim()) || [],
          correctOrder: responseData.orderingItems?.filter(i => i.text.trim()).map(i => i.id) || []
        };
      default:
        return {};
    }
  };

  // Update param config when function type changes
  useEffect(() => {
    if (createType === 'dynamic' && !editingQuestion) {
      setDynamicParamConfig(getDefaultParamConfig(dynamicFunctionType));
    }
  }, [dynamicFunctionType, createType, editingQuestion]);

  // Generate dynamic preview
  const buildCustomStatementsPayload = () => {
    const errors = [];
    const statements = (dynamicCustomStatements || []).map((statement, index) => {
      let predicate;
      try {
        predicate = statement.predicateText ? JSON.parse(statement.predicateText) : { always: true };
      } catch (err) {
        errors.push(`Custom statement ${index + 1} has invalid predicate JSON.`);
        predicate = null;
      }

      return {
        id: statement.id || `custom_${index}`,
        text: statement.text || '',
        category: statement.category || StatementCategory.STRUCTURAL,
        type: statement.type || StatementType.CONDITIONAL,
        difficulty: typeof statement.difficulty === 'number' ? statement.difficulty : 2,
        predicate,
        targetSkill: statement.targetSkill || null,
        misconception: statement.misconception || null,
        functionTypes: [dynamicFunctionType]
      };
    }).filter(statement => statement.text && statement.predicate);

    return { statements, errors };
  };

  const generateDynamicPreview = useCallback(() => {
    try {
      const { statements, errors } = buildCustomStatementsPayload();
      if (errors.length > 0) {
        setDynamicPreview(null);
        return;
      }
      const template = {
        id: 'preview',
        functionType: dynamicFunctionType,
        parameterConfig: dynamicParamConfig,
        statementConfig: dynamicStatementConfig,
        displayConfig: { showGraph: true, showFeatures: ['vertex', 'intercepts', 'asymptotes'] },
        subject: questionCategories.subject,
        grades: questionCategories.grades,
        topics: questionCategories.topics,
        marks: dynamicMarks,
        customStatements: statements,
        statementSource: dynamicStatementSource
      };

      const builder = new DynamicQuestionInstanceBuilder();
      const instance = builder.buildInstance(template, dynamicPreviewSeed);

      if (instance.success) {
        setDynamicPreview(instance);
      } else {
        setDynamicPreview(null);
      }
    } catch (err) {
      console.error('Preview generation error:', err);
    }
  }, [
    dynamicFunctionType,
    dynamicParamConfig,
    dynamicStatementConfig,
    dynamicMarks,
    dynamicPreviewSeed,
    questionCategories,
    dynamicCustomStatements,
    dynamicStatementSource
  ]);

  useEffect(() => {
    if (createType === 'dynamic') {
      generateDynamicPreview();
    }
  }, [createType, generateDynamicPreview]);

  const handleRegenerateDynamicPreview = () => setDynamicPreviewSeed(Date.now());

  const resetDynamicForm = () => {
    setDynamicFunctionType('quadratic');
    setDynamicParamConfig(getDefaultParamConfig('quadratic'));
    setDynamicStatementConfig({
      includeCategories: Object.values(StatementCategory),
      totalOptions: { min: 5, max: 6 },
      correctOptions: { min: 2, max: 4 },
      incorrectOptions: { min: 2, max: 3 },
      requiredCategories: [StatementCategory.STRUCTURAL],
      targetDifficulty: { min: 1, max: 3 }
    });
    setDynamicMarks(4);
    setDynamicPreview(null);
    setDynamicCustomStatements([]);
    setDynamicStatementSource('merged');
    setSelectedDynamicTemplateId('');
    setDynamicTemplateName('');
    setDynamicTemplateError(null);
    setQuestionCategories({ subject: null, grades: [], topics: [], subtopics: [] });
    setError(null);
  };

  const handleDynamicSubmit = async (e) => {
    e.preventDefault();

    if (!questionCategories.subject) {
      setError('Please select a subject');
      return;
    }

    if (questionCategories.grades.length === 0) {
      setError('Please select at least one grade');
      return;
    }

    try {
      const { statements, errors } = buildCustomStatementsPayload();
      if (errors.length > 0) {
        setError('Fix custom statement predicates before saving.');
        return;
      }
      const dynamicQuestionData = {
        type: 'dynamic',
        functionType: dynamicFunctionType,
        parameterConfig: dynamicParamConfig,
        statementConfig: dynamicStatementConfig,
        customStatements: statements,
        statementSource: dynamicStatementSource,
        templateId: selectedDynamicTemplateId || null,
        displayConfig: {
          showGraph: true,
          showFeatures: ['vertex', 'intercepts', 'asymptotes', 'axis'],
          pedagogicalMode: true
        },
        subject: questionCategories.subject,
        grades: questionCategories.grades,
        topics: questionCategories.topics,
        subtopics: questionCategories.subtopics,
        marks: dynamicMarks,
        sampleInstance: dynamicPreview ? {
          equation: dynamicPreview.equation,
          params: dynamicPreview.params
        } : null
      };

      if (editingQuestion) {
        await updateQuestion(editingQuestion.id, dynamicQuestionData);
        setEditingQuestion(null);
      } else {
        await addQuestion(dynamicQuestionData);
      }

      resetDynamicForm();
    } catch (err) {
      setError('Failed to save dynamic question template');
      console.error(err);
    }
  };

  const toggleDynamicCategory = (category) => {
    setDynamicStatementConfig(prev => ({
      ...prev,
      includeCategories: prev.includeCategories.includes(category)
        ? prev.includeCategories.filter(c => c !== category)
        : [...prev.includeCategories, category]
    }));
  };

  const toggleDynamicRequiredCategory = (category) => {
    setDynamicStatementConfig(prev => ({
      ...prev,
      requiredCategories: prev.requiredCategories.includes(category)
        ? prev.requiredCategories.filter(c => c !== category)
        : [...prev.requiredCategories, category]
    }));
  };

  const availableTemplates = useMemo(() => getTemplatesForFunction(dynamicFunctionType), [dynamicFunctionType]);

  const templatesByCategory = useMemo(() => {
    const grouped = {};
    for (const cat of Object.values(StatementCategory)) {
      grouped[cat] = availableTemplates.filter(t => t.category === cat);
    }
    return grouped;
  }, [availableTemplates]);

  const addCustomStatement = () => {
    setDynamicCustomStatements(prev => ([
      ...prev,
      {
        id: `custom_${Date.now()}`,
        text: '',
        category: StatementCategory.STRUCTURAL,
        type: StatementType.CONDITIONAL,
        difficulty: 2,
        predicateText: JSON.stringify({ always: true }, null, 2),
        targetSkill: '',
        misconception: ''
      }
    ]));
  };

  const updateCustomStatement = (id, patch) => {
    setDynamicCustomStatements(prev => prev.map(statement =>
      statement.id === id ? { ...statement, ...patch } : statement
    ));
  };

  const removeCustomStatement = (id) => {
    setDynamicCustomStatements(prev => prev.filter(statement => statement.id !== id));
  };

  const applyDynamicTemplate = (template) => {
    if (!template) return;
    setSelectedDynamicTemplateId(template.id || '');
    setDynamicTemplateName(template.name || '');
    setDynamicFunctionType(template.functionType || 'quadratic');
    setDynamicParamConfig(template.parameterConfig || getDefaultParamConfig(template.functionType || 'quadratic'));
    setDynamicStatementConfig(template.statementConfig || {
      includeCategories: Object.values(StatementCategory),
      totalOptions: { min: 5, max: 6 },
      correctOptions: { min: 2, max: 4 },
      incorrectOptions: { min: 2, max: 3 },
      requiredCategories: [StatementCategory.STRUCTURAL],
      targetDifficulty: { min: 1, max: 3 }
    });
    setDynamicMarks(template.marks || 4);
    setDynamicCustomStatements(
      (template.customStatements || []).map((statement, index) => ({
        id: statement.id || `custom_${index}`,
        text: statement.text || '',
        category: statement.category || StatementCategory.STRUCTURAL,
        type: statement.type || StatementType.CONDITIONAL,
        difficulty: typeof statement.difficulty === 'number' ? statement.difficulty : 2,
        predicateText: JSON.stringify(statement.predicate || { always: true }, null, 2),
        targetSkill: statement.targetSkill || '',
        misconception: statement.misconception || ''
      }))
    );
    setDynamicStatementSource(template.statementSource || 'merged');
    setQuestionCategories({
      subject: template.subject || null,
      grades: template.grades || [],
      topics: template.topics || [],
      subtopics: template.subtopics || []
    });
  };

  const handleSaveDynamicTemplate = async () => {
    if (!dynamicTemplateName.trim()) {
      setDynamicTemplateError('Template name is required.');
      return;
    }

    const { statements, errors } = buildCustomStatementsPayload();
    if (errors.length > 0) {
      setDynamicTemplateError('Fix custom statement predicates before saving template.');
      return;
    }

    setIsSavingTemplate(true);
    setDynamicTemplateError(null);
    try {
      const payload = {
        id: selectedDynamicTemplateId || undefined,
        name: dynamicTemplateName.trim(),
        functionType: dynamicFunctionType,
        parameterConfig: dynamicParamConfig,
        statementConfig: dynamicStatementConfig,
        customStatements: statements,
        statementSource: dynamicStatementSource,
        subject: questionCategories.subject,
        grades: questionCategories.grades,
        topics: questionCategories.topics,
        subtopics: questionCategories.subtopics,
        marks: dynamicMarks
      };

      const result = await leitnerService.saveDynamicTemplate(bookId, payload);
      const templates = await leitnerService.getDynamicTemplates(bookId);
      setDynamicTemplates(templates);
      setSelectedDynamicTemplateId(result.id);
    } catch (err) {
      console.error('Failed to save dynamic template:', err);
      setDynamicTemplateError('Failed to save template.');
    } finally {
      setIsSavingTemplate(false);
    }
  };


  const handleCancelEdit = () => {
    setEditingQuestion(null);
    resetStemForm();
    resetDynamicForm();
    resetSimpleForm();
  };

  const handleStartEdit = (question) => {

    if (question.type === 'dynamic') {
      setCreateType('dynamic');
      setDynamicFunctionType(question.functionType);
      setDynamicParamConfig(question.parameterConfig || getDefaultParamConfig(question.functionType));
      setDynamicStatementConfig(question.statementConfig || {
        includeCategories: Object.values(StatementCategory),
        totalOptions: { min: 5, max: 6 },
        correctOptions: { min: 2, max: 4 },
        incorrectOptions: { min: 2, max: 3 },
        requiredCategories: [],
        targetDifficulty: { min: 1, max: 3 }
      });
      setDynamicMarks(question.marks || 4);
      setDynamicCustomStatements(
        (question.customStatements || []).map((statement, index) => ({
          id: statement.id || `custom_${index}`,
          text: statement.text || '',
          category: statement.category || StatementCategory.STRUCTURAL,
          type: statement.type || StatementType.CONDITIONAL,
          difficulty: typeof statement.difficulty === 'number' ? statement.difficulty : 2,
          predicateText: JSON.stringify(statement.predicate || { always: true }, null, 2),
          targetSkill: statement.targetSkill || '',
          misconception: statement.misconception || ''
        }))
      );
      setDynamicStatementSource(question.statementSource || 'merged');
      setSelectedDynamicTemplateId(question.templateId || '');
      setDynamicTemplateName('');
      setEditingQuestion(question);
      return; // Early return before simple/stem handling
    }
    setEditingQuestion(question);
    setError(null);

    // Prefill categories - handle both old single grade and new multi-grade format
    setQuestionCategories({
      subject: question.subject || null,
      grades: Array.isArray(question.grades) ? question.grades : (question.grade ? [question.grade] : []),
      topics: question.topics || [],
      subtopics: question.subtopics || []
    });

    document.getElementById('creator-form-container')?.scrollIntoView({ behavior: 'smooth' });

    if (question.type === 'simple') {
      setCreateType('simple');
      setQuestionStatus(question.status || 'draft');
    } else {
      // ADD THIS ELSE BRANCH FOR STEM QUESTIONS
      setCreateType('stem');
      setStemText(question.stemText);
      setStemMedia(question.media || { type: 'none', images: [], video: null, embed: null });

      // Map subquestions with their individual metadata and response types
      const subsWithMetadata = JSON.parse(JSON.stringify(question.subQuestions || [])).map(sq => ({
        text: sq.text || '',
        responseType: sq.responseType || RESPONSE_TYPES.MULTISELECT,
        // Multiselect fields
        options: sq.options || [{ id: 'opt_1', text: '' }, { id: 'opt_2', text: '' }],
        correctOptionIds: sq.correctOptionIds || [],
        // Short answer fields
        correctAnswer: sq.correctAnswer || '',
        acceptableAnswers: sq.acceptableAnswers || [],
        caseSensitive: sq.caseSensitive || false,
        // Paragraph fields
        rubric: sq.rubric || '',
        minWords: sq.minWords || 0,
        maxWords: sq.maxWords || 0,
        // Matching fields
        matchingPairs: sq.matchingPairs || [{ id: 'pair_1', prompt: '', match: '' }, { id: 'pair_2', prompt: '', match: '' }],
        // Ordering fields
        orderingItems: sq.orderingItems || [{ id: 'order_1', text: '' }, { id: 'order_2', text: '' }],
        // Common metadata
        level: sq.level || 1,
        marks: sq.marks || 1,
        hint: sq.hint && typeof sq.hint === 'object'
          ? { text: sq.hint.text || '', audio: sq.hint.audio || null, video: sq.hint.video || null }
          : { text: sq.hint || '', audio: null, video: null },
        requiredKnowledge: sq.requiredKnowledge || { skills: [], concepts: [] },
        subtopics: sq.subtopics || []
      }));
      setSubQuestions(subsWithMetadata);
      setQuestionStatus(question.status || 'draft');
    }
  };

  const handleDuplicateQuestion = async (question) => {
    try {
      const { id, _id, createdAt, updatedAt, ...rest } = question;
      await addQuestion({ ...rest });
    } catch (err) {
      console.error('Failed to duplicate question:', err);
      setError('Failed to duplicate question');
    }
  };

  const handleAddOption = (setter) => {
    setter(prev => prev.length < 6 ? [...prev, { id: `opt_${Date.now()}`, text: '' }] : prev);
  };

  const handleRemoveOption = (id, optionsSetter, correctIdSetter) => {
    optionsSetter(prev => prev.length > 2 ? prev.filter(opt => opt.id !== id) : prev);
    correctIdSetter(prev => prev.filter(correctId => correctId !== id));
  };

  const handleOptionTextChange = (id, text, setter) => {
    setter(prev => prev.map(opt => (opt.id === id ? { ...opt, text } : opt)));
  };

  const handleCorrectOptionToggle = (id, setter) => {
    setter(prev => prev.includes(id) ? prev.filter(correctId => correctId !== id) : [...prev, id]);
  };

  const moveOption = (index, direction, setter) => {
    setter(prev => {
      if ((direction === 'up' && index === 0) || (direction === 'down' && index === prev.length - 1)) return prev;
      const newOptions = [...prev];
      const item = newOptions[index];
      const swapIndex = direction === 'up' ? index - 1 : index + 1;
      newOptions[index] = newOptions[swapIndex];
      newOptions[swapIndex] = item;
      return newOptions;
    });
  };

  const addSubQuestion = () => {
    if (subQuestions.length >= 10) return;
    setSubQuestions(prev => [...prev, {
      text: '',
      responseType: RESPONSE_TYPES.MULTISELECT,
      responseData: {
        options: [{ id: `opt_${Date.now()}_1`, text: '' }, { id: `opt_${Date.now()}_2`, text: '' }],
        correctOptionIds: []
      },
      level: 1,
      marks: 1,
      hint: { text: '', audio: null, video: null },
      requiredKnowledge: { skills: [], concepts: [] },
      subtopics: []
    }]);
  };

  const updateSubQuestionResponseType = (index, responseType) => {
    setSubQuestions(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        responseType,
        responseData: getDefaultResponseData(responseType)
      };
      return updated;
    });
  };

  const updateSubQuestionResponseData = (index, responseData) => {
    setSubQuestions(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], responseData };
      return updated;
    });
  };

  const getDefaultResponseData = (responseType) => {
    switch (responseType) {
      case RESPONSE_TYPES.MULTISELECT:
        return { options: [{ id: `opt_${Date.now()}_1`, text: '' }, { id: `opt_${Date.now()}_2`, text: '' }], correctOptionIds: [] };
      case RESPONSE_TYPES.SHORT_ANSWER:
        return { correctAnswer: '', acceptableAnswers: [], caseSensitive: false };
      case RESPONSE_TYPES.PARAGRAPH:
        return { rubric: '', minWords: 0, maxWords: 0 };
      case RESPONSE_TYPES.MATCHING:
        return { matchingPairs: [{ id: 'pair_1', prompt: '', match: '' }, { id: 'pair_2', prompt: '', match: '' }] };
      case RESPONSE_TYPES.ORDERING:
        return { orderingItems: [{ id: 'order_1', text: '' }, { id: 'order_2', text: '' }, { id: 'order_3', text: '' }] };
      default:
        return {};
    }
  };

  const removeSubQuestion = (index) => {
    if (subQuestions.length <= 1) return;
    setSubQuestions(prev => prev.filter((_, i) => i !== index));
  };

  const updateSubQuestionField = (index, field, value) => {
    setSubQuestions(prev => prev.map((sq, i) =>
      i === index ? { ...sq, [field]: value } : sq
    ));
  };

  const updateSubQuestionText = (index, text) => updateSubQuestionField(index, 'text', text);
  const updateSubQuestionOptions = (index, newOptions) => updateSubQuestionField(index, 'options', newOptions);
  const updateSubQuestionCorrectIds = (index, newCorrectIds) => updateSubQuestionField(index, 'correctOptionIds', newCorrectIds);







  // Updated QuestionCategoryForm with multi-grade and subtopics
  const QuestionCategoryFormUpdated = ({ value, onChange, availableCategories, onAddSessionCategory }) => {
    const [newSubject, setNewSubject] = useState('');
    const [newGrade, setNewGrade] = useState('');
    const [newTopic, setNewTopic] = useState('');
    const [newSubtopic, setNewSubtopic] = useState('');

    const addNewSubject = () => {
      if (newSubject.trim() && !availableCategories.subjects?.includes(newSubject.trim())) {
        const trimmed = newSubject.trim();
        onChange({ ...value, subject: trimmed });
        onAddSessionCategory?.('subjects', trimmed);
        setNewSubject('');
      }
    };

    const addNewGrade = () => {
      if (newGrade.trim() && !value.grades.includes(newGrade.trim())) {
        const trimmed = newGrade.trim();
        onChange({ ...value, grades: [...value.grades, trimmed] });
        onAddSessionCategory?.('grades', trimmed);
        setNewGrade('');
      }
    };

    const addNewTopic = () => {
      if (newTopic.trim() && !value.topics.includes(newTopic.trim())) {
        const trimmed = newTopic.trim();
        onChange({ ...value, topics: [...value.topics, trimmed] });
        onAddSessionCategory?.('topics', trimmed);
        setNewTopic('');
      }
    };

    const addNewSubtopic = () => {
      if (newSubtopic.trim() && !value.subtopics?.includes(newSubtopic.trim())) {
        const trimmed = newSubtopic.trim();
        onChange({ ...value, subtopics: [...(value.subtopics || []), trimmed] });
        onAddSessionCategory?.('subtopics', trimmed);
        setNewSubtopic('');
      }
    };

    return (
      <div className="mb-4 p-4 bg-slate-700/30 rounded-lg border border-slate-600">
        <h3 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
          <Tag size={16} className="text-indigo-400" />
          Question Categories
        </h3>

        {/* Subject */}
        <div className="mb-3">
          <label className="block text-sm font-medium text-slate-300 mb-1 flex items-center gap-1">
            <BookOpen size={14} className="text-purple-400" />
            Subject
          </label>
          <div className="flex flex-wrap gap-2 mb-2 bg-slate-700/50 p-2 rounded-md max-h-24 overflow-y-auto">
            {availableCategories.subjects?.length > 0 ? (
              availableCategories.subjects.map(subject => (
                <button
                  key={subject}
                  type="button"
                  onClick={() => onChange({ ...value, subject: value.subject === subject ? null : subject })}
                  className={`px-3 py-1 rounded-md text-sm transition ${value.subject === subject
                    ? 'bg-purple-600 text-white'
                    : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                    }`}
                >
                  {subject}
                </button>
              ))
            ) : (
              <span className="text-xs text-slate-400">No subjects yet. Add one below.</span>
            )}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newSubject}
              onChange={(e) => setNewSubject(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addNewSubject())}
              placeholder="Add new subject..."
              className="flex-1 bg-slate-700 text-sm border border-slate-600 rounded-md p-2 text-slate-100"
            />
            <button type="button" onClick={addNewSubject} className="px-3 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-sm">
              Add
            </button>
          </div>
        </div>

        {/* Multi-Grade Selection */}
        <div className="mb-3">
          <label className="block text-sm font-medium text-slate-300 mb-1">Grades (Select multiple)</label>
          <div className="flex flex-wrap gap-2 mb-2 bg-slate-700/50 p-2 rounded-md max-h-24 overflow-y-auto">
            {availableCategories.grades?.length > 0 ? (
              availableCategories.grades.map(grade => (
                <button
                  key={grade}
                  type="button"
                  onClick={() => {
                    const newGrades = value.grades.includes(grade)
                      ? value.grades.filter(g => g !== grade)
                      : [...value.grades, grade];
                    onChange({ ...value, grades: newGrades });
                  }}
                  className={`px-3 py-1 rounded-md text-sm transition ${value.grades.includes(grade)
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                    }`}
                >
                  {grade}
                </button>
              ))
            ) : (
              <span className="text-xs text-slate-400">No grades yet. Add one below.</span>
            )}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newGrade}
              onChange={(e) => setNewGrade(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addNewGrade())}
              placeholder="Add new grade..."
              className="flex-1 bg-slate-700 text-sm border border-slate-600 rounded-md p-2 text-slate-100"
            />
            <button type="button" onClick={addNewGrade} className="px-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm">
              Add
            </button>
          </div>
        </div>

        {/* Topics */}
        <div className="mb-3">
          <label className="block text-sm font-medium text-slate-300 mb-1">Topics</label>
          <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto mb-2 bg-slate-700/50 p-2 rounded-md">
            {availableCategories.topics?.length > 0 ? (
              availableCategories.topics.map(topic => (
                <button
                  key={topic}
                  type="button"
                  onClick={() => {
                    const newTopics = value.topics.includes(topic)
                      ? value.topics.filter(t => t !== topic)
                      : [...value.topics, topic];
                    onChange({ ...value, topics: newTopics });
                  }}
                  className={`px-2 py-1 rounded text-xs transition ${value.topics.includes(topic)
                    ? 'bg-green-600 text-white'
                    : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                    }`}
                >
                  {topic}
                </button>
              ))
            ) : (
              <span className="text-xs text-slate-400">No topics yet. Add one below.</span>
            )}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newTopic}
              onChange={(e) => setNewTopic(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addNewTopic())}
              placeholder="Add new topic..."
              className="flex-1 bg-slate-700 text-sm border border-slate-600 rounded-md p-2 text-slate-100"
            />
            <button type="button" onClick={addNewTopic} className="px-3 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm">
              Add
            </button>
          </div>
        </div>

        {/* Subtopics */}
        <div className="mb-2">
          <label className="block text-sm font-medium text-slate-300 mb-1 flex items-center gap-1">
            <Layers size={14} className="text-teal-400" />
            Subtopics
          </label>
          <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto mb-2 bg-slate-700/50 p-2 rounded-md">
            {availableCategories.subtopics?.length > 0 ? (
              availableCategories.subtopics.map(subtopic => (
                <button
                  key={subtopic}
                  type="button"
                  onClick={() => {
                    const current = value.subtopics || [];
                    const newSubtopics = current.includes(subtopic)
                      ? current.filter(s => s !== subtopic)
                      : [...current, subtopic];
                    onChange({ ...value, subtopics: newSubtopics });
                  }}
                  className={`px-2 py-1 rounded text-xs transition ${value.subtopics?.includes(subtopic)
                    ? 'bg-teal-600 text-white'
                    : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                    }`}
                >
                  {subtopic}
                </button>
              ))
            ) : (
              <span className="text-xs text-slate-400">No subtopics yet. Add one below.</span>
            )}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newSubtopic}
              onChange={(e) => setNewSubtopic(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addNewSubtopic())}
              placeholder="Add new subtopic..."
              className="flex-1 bg-slate-700 text-sm border border-slate-600 rounded-md p-2 text-slate-100"
            />
            <button type="button" onClick={addNewSubtopic} className="px-3 bg-teal-600 hover:bg-teal-700 text-white rounded-md text-sm">
              Add
            </button>
          </div>
        </div>
      </div>
    );
  };

  const QuestionMediaPreview = ({ media }) => {
    if (!media || media.type === 'none') return null;

    return (
      <div className="my-2">
        <QuestionMediaDisplay media={media} className="max-w-md" />
      </div>
    );
  };

  const getLevelBadge = (level) => {
    const levelInfo = DIFFICULTY_LEVELS.find(l => l.value === level) || DIFFICULTY_LEVELS[0];
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${levelInfo.color} text-white`}>
        L{level}
      </span>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div id="creator-form-container" className="bg-slate-800 p-6 rounded-lg shadow-xl">
        <h2 className="text-2xl font-bold mb-6 text-indigo-400">
          {editingQuestion ? 'Edit Question' : 'Create New Question'}
        </h2>

        {error && (
          <div className="mb-4 p-3 bg-red-900/30 border border-red-500 rounded-md text-red-200">
            {error}
          </div>
        )}

        {editingQuestion && (
          <div className="mb-4 p-3 bg-blue-900/30 border border-blue-500 rounded-md flex justify-between items-center">
            <p className="text-blue-200 text-sm">
              Editing: {editingQuestion.type === 'simple'
                ? `"${editingQuestion.text.substring(0, 30)}..."`
                : `Stem: "${editingQuestion.stemText.substring(0, 30)}..."`}
            </p>
            <button
              onClick={handleCancelEdit}
              className="text-sm text-slate-300 hover:text-white font-semibold flex items-center"
            >
              &times; <span className="ml-1">Cancel</span>
            </button>
          </div>
        )}

        <div className="flex space-x-2 mb-6">
          <button
            onClick={() => !editingQuestion && setCreateType('simple')}
            disabled={editingQuestion && editingQuestion.type !== 'simple'}
            className={`flex-1 py-2 px-3 rounded-md font-medium transition text-sm ${createType === 'simple'
              ? 'bg-indigo-600 text-white'
              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              } ${editingQuestion && editingQuestion.type !== 'simple' ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Simple
          </button>
          <button
            onClick={() => !editingQuestion && setCreateType('stem')}
            disabled={editingQuestion && editingQuestion.type !== 'stem'}
            className={`flex-1 py-2 px-3 rounded-md font-medium transition text-sm ${createType === 'stem'
              ? 'bg-indigo-600 text-white'
              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              } ${editingQuestion && editingQuestion.type !== 'stem' ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Stem
          </button>
          <button
            onClick={() => !editingQuestion && setCreateType('dynamic')}
            disabled={editingQuestion && editingQuestion.type !== 'dynamic'}
            className={`flex-1 py-2 px-3 rounded-md font-medium transition text-sm flex items-center justify-center gap-1 ${createType === 'dynamic'
              ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white'
              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              } ${editingQuestion && editingQuestion.type !== 'dynamic' ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Wand2 size={14} />
            Dynamic
          </button>
        </div>

        <QuestionCategoryFormUpdated
          value={questionCategories}
          onChange={setQuestionCategories}
          availableCategories={mergedCategories}
          onAddSessionCategory={(type, value) => {
            setSessionCategories(prev => ({
              ...prev,
              [type]: prev[type].includes(value) ? prev[type] : [...prev[type], value]
            }));
          }}
        />

        {createType === 'simple' ? (
          <SimpleQuestionForm
            key={simpleFormKey}
            initialData={editingQuestion?.type === 'simple' ? {
              ...editingQuestion,
              responseData: buildResponseTypeData(editingQuestion.responseType || RESPONSE_TYPES.MULTISELECT, {
                options: editingQuestion.options,
                correctOptionIds: editingQuestion.correctOptionIds,
                correctAnswer: editingQuestion.correctAnswer,
                acceptableAnswers: editingQuestion.acceptableAnswers,
                caseSensitive: editingQuestion.caseSensitive,
                rubric: editingQuestion.rubric,
                minWords: editingQuestion.minWords,
                maxWords: editingQuestion.maxWords,
                matchingPairs: editingQuestion.matchingPairs,
                orderingItems: editingQuestion.orderingItems
              })
            } : null}
            onSave={async (data) => {
              const fullData = {
                ...data,
                subject: questionCategories.subject,
                grades: questionCategories.grades,
                topics: questionCategories.topics,
                subtopics: questionCategories.subtopics,
                ...buildResponseTypeData(data.responseType, data.responseData)
              };
              delete fullData.responseData;

              if (editingQuestion) {
                await updateQuestion(editingQuestion.id, fullData);
                setEditingQuestion(null);
              } else {
                await addQuestion(fullData);
              }
              resetStemForm();
              resetSimpleForm();
            }}
            onCancel={editingQuestion ? handleCancelEdit : null}
            bookId={bookId}
            availableQuestions={questions}
            onQuickCreate={handleQuickCreateQuestion}
          />
        ) : createType === 'stem' ? (
          <form onSubmit={handleStemSubmit} className="space-y-4" id="creator-form">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Stem Text (Shared Prompt)
              </label>
              <InlineMathField
                value={stemText}
                onChange={setStemText}
                rows={3}
                className="w-full bg-slate-700 text-sm border border-slate-600 rounded-md p-2 text-slate-100 focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter the shared stem/prompt for all sub-questions... (Ctrl+M for math)"
              />

              {/* Response Type Selector 
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Response Type</label>
                <ResponseTypeSelector 
                  value={simpleResponseType} 
                  onChange={(type) => {
                    setSimpleResponseType(type);
                    setSimpleResponseData(getDefaultResponseData(type));
                  }}
                />
              </div>

              {/* Response Input based on type
              <ResponseInput
                responseType={simpleResponseType}
                responseData={simpleResponseData}
                setResponseData={setSimpleResponseData}
              /> */}
            </div>

            <QuestionMediaSection
              value={stemMedia}
              onChange={setStemMedia}
              label="Stem Media (Optional)"
            />

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-3">Sub-Questions (Each with its own metadata)</label>
              {subQuestions.map((sq, sqIndex) => (
                <div key={sqIndex} className="mb-6 p-4 bg-slate-700/50 rounded-md border border-slate-600">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-medium text-slate-200">Sub-question {sqIndex + 1}</h4>
                    {subQuestions.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSubQuestion(sqIndex)}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>

                  <InlineMathField
                    value={sq.text}
                    onChange={(text) => updateSubQuestionText(sqIndex, text)}
                    rows={2}
                    className="w-full bg-slate-700 text-sm border border-slate-600 rounded-md p-2 text-slate-100 focus:ring-2 focus:ring-indigo-500 mb-3"
                    placeholder={`Sub-question ${sqIndex + 1} text... (Ctrl+M for math)`}
                  />

                  {/* Per-subquestion metadata */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <DifficultySelector
                      value={sq.level}
                      onChange={(level) => updateSubQuestionField(sqIndex, 'level', level)}
                      compact
                    />
                    <MarksInput
                      value={sq.marks}
                      onChange={(marks) => updateSubQuestionField(sqIndex, 'marks', marks)}
                      compact
                    />
                  </div>

                  <HintSection
                    value={sq.hint}
                    onChange={(hint) => updateSubQuestionField(sqIndex, 'hint', hint)}
                    bookId={bookId}
                    compact
                  />

                  <KnowledgeInput
                    value={sq.requiredKnowledge}
                    onChange={(knowledge) => updateSubQuestionField(sqIndex, 'requiredKnowledge', knowledge)}
                    compact
                  />

                  <SubtopicsInput
                    value={sq.subtopics || []}
                    onChange={(subtopics) => updateSubQuestionField(sqIndex, 'subtopics', subtopics)}
                    availableSubtopics={mergedCategories.subtopics || []}
                    onAddNew={(value) => {
                      setSessionCategories(prev => ({
                        ...prev,
                        subtopics: prev.subtopics.includes(value) ? prev.subtopics : [...prev.subtopics, value]
                      }));
                    }}
                  />

                  {/* Response Type Selector */}
                  <div className="mt-3">
                    <label className="block text-xs text-slate-400 mb-2">Response Type</label>
                    <ResponseTypeSelector
                      value={sq.responseType || RESPONSE_TYPES.MULTISELECT}
                      onChange={(type) => updateSubQuestionResponseType(sqIndex, type)}
                      compact={true}
                    />
                  </div>

                  {/* Response Input based on type */}
                  <div className="mt-3">
                    {sq.responseType === RESPONSE_TYPES.MULTISELECT && (
                      <MultiselectInput
                        options={sq.options || []}
                        setOptions={(options) => updateSubQuestionField(sqIndex, 'options', options)}
                        correctIds={sq.correctOptionIds || []}
                        setCorrectIds={(ids) => updateSubQuestionField(sqIndex, 'correctOptionIds', ids)}
                        compact={true}
                        availableQuestions={questions}
                        onQuickCreate={handleQuickCreateQuestion}
                        renderBranchCreator={(onSave, onCancel) => (
                          <div className="border border-indigo-500/30 rounded-lg p-3 bg-slate-800/50">
                            <SimpleQuestionForm
                              compact
                              onSave={onSave}
                              onCancel={onCancel}
                              bookId={bookId}
                              availableQuestions={questions}
                              onQuickCreate={handleQuickCreateQuestion}
                              useFormTag={false}
                            />
                          </div>
                        )}
                      />
                    )}

                    {sq.responseType === RESPONSE_TYPES.SHORT_ANSWER && (
                      <ShortAnswerInput
                        correctAnswer={sq.correctAnswer || ''}
                        setCorrectAnswer={(val) => updateSubQuestionField(sqIndex, 'correctAnswer', val)}
                        acceptableAnswers={sq.acceptableAnswers || []}
                        setAcceptableAnswers={(val) => updateSubQuestionField(sqIndex, 'acceptableAnswers', val)}
                        caseSensitive={sq.caseSensitive || false}
                        setCaseSensitive={(val) => updateSubQuestionField(sqIndex, 'caseSensitive', val)}
                        compact={true}
                      />
                    )}

                    {sq.responseType === RESPONSE_TYPES.PARAGRAPH && (
                      <ParagraphInput
                        rubric={sq.rubric || ''}
                        setRubric={(val) => updateSubQuestionField(sqIndex, 'rubric', val)}
                        minWords={sq.minWords || 0}
                        setMinWords={(val) => updateSubQuestionField(sqIndex, 'minWords', val)}
                        maxWords={sq.maxWords || 0}
                        setMaxWords={(val) => updateSubQuestionField(sqIndex, 'maxWords', val)}
                        compact={true}
                      />
                    )}

                    {sq.responseType === RESPONSE_TYPES.MATCHING && (
                      <MatchingInput
                        pairs={sq.matchingPairs || []}
                        setPairs={(pairs) => updateSubQuestionField(sqIndex, 'matchingPairs', pairs)}
                        compact={true}
                      />
                    )}

                    {sq.responseType === RESPONSE_TYPES.ORDERING && (
                      <OrderingInput
                        items={sq.orderingItems || []}
                        setItems={(items) => updateSubQuestionField(sqIndex, 'orderingItems', items)}
                        compact={true}
                      />
                    )}
                  </div>
                </div>
              ))}

              {subQuestions.length < 10 && (
                <button
                  type="button"
                  onClick={addSubQuestion}
                  className="mt-3 text-sm text-indigo-400 hover:text-indigo-300 flex items-center"
                >
                  <Plus size={16} className="mr-1" /> Add Sub-Question
                </button>
              )}
            </div>

            {/* Status Toggle & Submit */}
            <div className="space-y-3 pt-2 border-t border-slate-600">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-300">Status</span>
                <div className="flex rounded-lg overflow-hidden border border-slate-600">
                  <button
                    type="button"
                    onClick={() => setQuestionStatus('draft')}
                    className={`px-4 py-1.5 text-sm font-medium transition ${questionStatus === 'draft'
                      ? 'bg-amber-500 text-white'
                      : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                      }`}
                  >
                    Draft
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuestionStatus('published')}
                    className={`px-4 py-1.5 text-sm font-medium transition ${questionStatus === 'published'
                      ? 'bg-green-500 text-white'
                      : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                      }`}
                  >
                    Published
                  </button>
                </div>
              </div>
              <button
                type="submit"
                className={`w-full font-bold py-3 px-4 rounded-md transition ${questionStatus === 'published'
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-amber-600 hover:bg-amber-700 text-white'
                  }`}
              >
                {editingQuestion ? 'Update Stem Question' : `Save as ${questionStatus === 'published' ? 'Published' : 'Draft'}`}
              </button>
            </div>
          </form>
        ) : createType === 'dynamic' ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-400">
                Use templates and parameter ranges to generate adaptive questions.
              </div>
              <button
                type="button"
                onClick={() => setShowDynamicHelper(prev => !prev)}
                className="text-xs px-3 py-1.5 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-200 border border-slate-600"
              >
                {showDynamicHelper ? 'Hide Guide' : 'Show Guide'}
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <form onSubmit={handleDynamicSubmit} className="space-y-4">
                {/* Simplified Category Selection */}
                <div className="p-3 bg-slate-700/30 rounded-lg border border-slate-600">
                  <label className="block text-sm font-medium text-slate-300 mb-2">Subject & Grade</label>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={questionCategories.subject || ''}
                      onChange={(e) => setQuestionCategories(prev => ({ ...prev, subject: e.target.value || null }))}
                      className="bg-slate-700 text-white text-sm rounded px-2 py-1.5 border border-slate-600"
                    >
                      <option value="">Select Subject</option>
                      {mergedCategories.subjects.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    <select
                      value={questionCategories.grades[0] || ''}
                      onChange={(e) => setQuestionCategories(prev => ({
                        ...prev,
                        grades: e.target.value ? [e.target.value] : []
                      }))}
                      className="bg-slate-700 text-white text-sm rounded px-2 py-1.5 border border-slate-600"
                    >
                      <option value="">Select Grade</option>
                      {mergedCategories.grades.map(g => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Template Library */}
                <div className="p-3 bg-slate-700/30 rounded-lg border border-slate-600 space-y-3">
                  <label className="block text-sm font-medium text-slate-300">Template Library</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <select
                      value={selectedDynamicTemplateId}
                      onChange={(e) => {
                        const templateId = e.target.value;
                        setSelectedDynamicTemplateId(templateId);
                        setDynamicTemplateError(null);
                        const template = dynamicTemplates.find(t => t.id === templateId);
                        if (template) {
                          applyDynamicTemplate(template);
                        }
                      }}
                      className="bg-slate-700 text-white text-sm rounded px-2 py-1.5 border border-slate-600"
                    >
                      <option value="">Select saved template</option>
                      {dynamicTemplates.map(template => (
                        <option key={template.id} value={template.id}>{template.name || template.id}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={dynamicTemplateName}
                      onChange={(e) => setDynamicTemplateName(e.target.value)}
                      placeholder="Template name"
                      className="bg-slate-700 text-white text-sm rounded px-2 py-1.5 border border-slate-600"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleSaveDynamicTemplate}
                      disabled={isSavingTemplate}
                      className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-md disabled:opacity-60"
                    >
                      {selectedDynamicTemplateId ? 'Update Template' : 'Save Template'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedDynamicTemplateId('');
                        setDynamicTemplateName('');
                        setDynamicTemplateError(null);
                      }}
                      className="px-3 py-1.5 bg-slate-600 hover:bg-slate-500 text-slate-200 text-sm rounded-md"
                    >
                      New Template
                    </button>
                  </div>
                  {dynamicTemplateError && (
                    <div className="text-xs text-red-300">{dynamicTemplateError}</div>
                  )}
                </div>

                {/* Function Type Selector */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                    <Wand2 size={16} className="text-indigo-400" />
                    Function Type
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {FUNCTION_TYPES.map(ft => (
                      <button
                        key={ft.id}
                        type="button"
                        onClick={() => setDynamicFunctionType(ft.id)}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${dynamicFunctionType === ft.id
                          ? 'bg-indigo-600 border-indigo-500'
                          : 'bg-slate-700 border-slate-600 hover:border-slate-500'
                          }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{ft.icon}</span>
                          <span className="text-sm font-medium text-white">{ft.label}</span>
                        </div>
                        <p className="text-xs text-slate-300 font-mono mt-1">{ft.formula}</p>
                      </button>
                    ))}
                  </div>
                </div>

            {/* Statement Categories */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Statement Categories</label>
              <div className="space-y-2 max-h-48 overflow-y-auto bg-slate-700/30 p-3 rounded-lg">
                {Object.entries(templatesByCategory).map(([category, templates]) => {
                  if (templates.length === 0) return null;
                  const catInfo = CATEGORY_LABELS[category] || { label: category, color: 'bg-slate-500' };
                  const isIncluded = dynamicStatementConfig.includeCategories.includes(category);
                  const isRequired = dynamicStatementConfig.requiredCategories.includes(category);

                  return (
                    <div
                      key={category}
                      className={`p-2 rounded-lg border transition-colors ${isIncluded ? 'bg-indigo-500/20 border-indigo-500' : 'bg-slate-700/50 border-slate-600'
                        }`}
                    >
                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => toggleDynamicCategory(category)}
                          className="flex items-center gap-2 flex-1 text-left"
                        >
                          <div className={`w-3 h-3 rounded ${isIncluded ? catInfo.color : 'bg-slate-600'}`} />
                          <span className="text-sm text-white">{catInfo.label}</span>
                          <span className="text-xs text-slate-400">({templates.length})</span>
                        </button>
                        {isIncluded && (
                          <button
                            type="button"
                            onClick={() => toggleDynamicRequiredCategory(category)}
                            className={`text-xs px-2 py-0.5 rounded ${isRequired ? 'bg-amber-500 text-white' : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                              }`}
                          >
                            {isRequired ? 'Required' : 'Optional'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Statement Source */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Statement Source</label>
                <select
                  value={dynamicStatementSource}
                  onChange={(e) => setDynamicStatementSource(e.target.value)}
                  className="w-full bg-slate-700 text-white text-sm rounded px-2 py-1.5 border border-slate-600"
                >
                  <option value="merged">Built-in + Custom</option>
                  <option value="built_in">Built-in Only</option>
                  <option value="custom_only">Custom Only</option>
                </select>
              </div>
            </div>

            {/* Custom Statements */}
            <div className="bg-slate-700/30 rounded-lg p-3 border border-slate-600 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-300">Custom Statements</span>
                <button
                  type="button"
                  onClick={addCustomStatement}
                  className="text-xs px-2 py-1 bg-slate-600 hover:bg-slate-500 text-slate-200 rounded"
                >
                  Add Statement
                </button>
              </div>
              {dynamicCustomStatements.length === 0 ? (
                <p className="text-xs text-slate-400">No custom statements yet.</p>
              ) : (
                dynamicCustomStatements.map((statement, index) => (
                  <div key={statement.id} className="bg-slate-800/60 border border-slate-600 rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-400">Statement {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeCustomStatement(statement.id)}
                        className="text-xs text-red-300 hover:text-red-200"
                      >
                        Remove
                      </button>
                    </div>
                    <input
                      type="text"
                      value={statement.text}
                      onChange={(e) => updateCustomStatement(statement.id, { text: e.target.value })}
                      placeholder="Statement text. Example: The gradient is positive"
                      className="w-full bg-slate-700 text-white text-sm rounded px-2 py-1.5 border border-slate-600"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <select
                        value={statement.category}
                        onChange={(e) => updateCustomStatement(statement.id, { category: e.target.value })}
                        className="bg-slate-700 text-white text-sm rounded px-2 py-1.5 border border-slate-600"
                      >
                        {Object.values(StatementCategory).map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                      <select
                        value={statement.type}
                        onChange={(e) => updateCustomStatement(statement.id, { type: e.target.value })}
                        className="bg-slate-700 text-white text-sm rounded px-2 py-1.5 border border-slate-600"
                      >
                        {Object.values(StatementType).map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min={1}
                        max={5}
                        value={statement.difficulty}
                        onChange={(e) => updateCustomStatement(statement.id, { difficulty: parseInt(e.target.value, 10) })}
                        className="bg-slate-700 text-white text-sm rounded px-2 py-1.5 border border-slate-600"
                      />
                    </div>
                    <textarea
                      value={statement.predicateText}
                      onChange={(e) => updateCustomStatement(statement.id, { predicateText: e.target.value })}
                      rows={4}
                      className="w-full bg-slate-700 text-white text-xs rounded px-2 py-1.5 border border-slate-600 font-mono"
                      placeholder='Predicate JSON, e.g. {"gt":["params.m",0]}'
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={statement.targetSkill || ''}
                        onChange={(e) => updateCustomStatement(statement.id, { targetSkill: e.target.value })}
                        placeholder="Target skill (optional)"
                        className="bg-slate-700 text-white text-sm rounded px-2 py-1.5 border border-slate-600"
                      />
                      <input
                        type="text"
                        value={statement.misconception || ''}
                        onChange={(e) => updateCustomStatement(statement.id, { misconception: e.target.value })}
                        placeholder="Misconception tag (optional)"
                        className="bg-slate-700 text-white text-sm rounded px-2 py-1.5 border border-slate-600"
                      />
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Options & Marks */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Min Options</label>
                <input
                  type="number"
                  min={3}
                  max={8}
                  value={dynamicStatementConfig.totalOptions.min}
                  onChange={(e) => setDynamicStatementConfig(prev => ({
                    ...prev,
                    totalOptions: { ...prev.totalOptions, min: parseInt(e.target.value) }
                  }))}
                  className="w-full bg-slate-700 text-white text-sm rounded px-2 py-1.5 border border-slate-600"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Max Options</label>
                <input
                  type="number"
                  min={3}
                  max={8}
                  value={dynamicStatementConfig.totalOptions.max}
                  onChange={(e) => setDynamicStatementConfig(prev => ({
                    ...prev,
                    totalOptions: { ...prev.totalOptions, max: parseInt(e.target.value) }
                  }))}
                  className="w-full bg-slate-700 text-white text-sm rounded px-2 py-1.5 border border-slate-600"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Marks</label>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={dynamicMarks}
                  onChange={(e) => setDynamicMarks(parseInt(e.target.value))}
                  className="w-full bg-slate-700 text-white text-sm rounded px-2 py-1.5 border border-slate-600"
                />
              </div>
            </div>

            {/* Preview */}
            <div className="bg-slate-700/30 rounded-lg p-3 border border-slate-600">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-300 flex items-center gap-2">
                  <Eye size={14} />
                  Live Preview
                </span>
                <button
                  type="button"
                  onClick={handleRegenerateDynamicPreview}
                  className="text-xs px-2 py-1 bg-slate-600 hover:bg-slate-500 text-slate-300 rounded flex items-center gap-1"
                >
                  <RefreshCw size={12} />
                  Regenerate
                </button>
              </div>

              {dynamicPreview ? (
                <div className="space-y-2">
                  {dynamicPreview.graphConfig && (
                    <div className="bg-white rounded overflow-hidden">
                      <GraphSketchCompact
                        graphConfig={dynamicPreview.graphConfig}
                        equation={dynamicPreview.equation}
                        height={350}
                      />
                    </div>
                  )}

                  <div className="space-y-1">
                    {dynamicPreview.options?.slice(0, 3).map((opt, i) => (
                      <div
                        key={i}
                        className={`text-xs p-1.5 rounded flex items-center gap-2 ${opt.isCorrect ? 'bg-green-500/20 text-green-300' : 'bg-slate-600/50 text-slate-400'
                          }`}
                      >
                        {opt.isCorrect ? <CheckCircle size={12} /> : <div className="w-3 h-3 rounded-full border border-slate-500" />}
                        <span className="truncate">{opt.text}</span>
                      </div>
                    ))}
                    {dynamicPreview.options?.length > 3 && (
                      <p className="text-xs text-slate-500 text-center">+{dynamicPreview.options.length - 3} more...</p>
                    )}
                  </div>

                  <div className="flex justify-between text-xs text-slate-400 pt-2 border-t border-slate-600">
                    <span>✓ {dynamicPreview.validation?.correctCount} correct</span>
                    <span>✗ {dynamicPreview.validation?.incorrectCount} incorrect</span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-4">Generating preview...</p>
              )}
            </div>

                <button
                  type="submit"
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Wand2 size={18} />
                  {editingQuestion ? 'Update Dynamic Question' : 'Create Dynamic Question'}
                </button>
              </form>
            </div>

            {showDynamicHelper && (
              <div className="fixed inset-0 z-50">
                <div
                  className="absolute inset-0 bg-black/40"
                  onClick={() => setShowDynamicHelper(false)}
                />
                <aside className="absolute right-0 top-0 h-full w-full max-w-sm bg-slate-900 border-l border-slate-700 shadow-xl">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
                    <div className="text-sm font-semibold text-slate-200">Dynamic Question Guide</div>
                    <button
                      type="button"
                      onClick={() => setShowDynamicHelper(false)}
                      className="text-xs px-2 py-1 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-200"
                    >
                      Close
                    </button>
                  </div>
                  <div className="p-4 space-y-3 text-xs">
                    <div className="text-slate-400">
                      Build a question by defining a formula, parameter ranges, and statements that must be true or false.
                    </div>
                    <div className="text-slate-300 space-y-2">
                      <div>
                        1. Choose a function type and set parameter ranges that define variability.
                      </div>
                      <div>
                        2. Use statement categories to control the kinds of facts the learner evaluates.
                      </div>
                      <div>
                        3. Add custom statements with predicate JSON. Example: <code>{"{\"gt\":[\"params.m\",0]}"}</code>.
                      </div>
                      <div>
                        4. Select a statement source to use built-in rules, your own, or both.
                      </div>
                      <div>
                        5. Save as a template to reuse the same structure with different parameters.
                      </div>
                    </div>
                    <div className="text-slate-500">
                      Tip: Keep min/max option counts aligned with the number of true/false statements you expect.
                    </div>
                  </div>
                </aside>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Question Bank */}
      <div id="question-bank" className="bg-slate-800 p-6 rounded-lg shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-indigo-400">Question Bank</h2>
          <span className="text-sm text-slate-400">
            {filteredQuestions.length} of {questions.length} questions
          </span>
        </div>

        <CategoryFilter
          categories={categories}
          selectedFilters={filters}
          onFilterChange={setFilters}
          onClear={() => setFilters({ subject: null, grades: [], topics: [] })}
          questions={questions}
        />

        <div className="space-y-4 max-h-[100vh] overflow-y-auto pr-2">
          {filteredQuestions.length === 0 ? (
            <p className="text-slate-400 text-center py-8">
              {questions.length === 0 ? 'No questions created yet.' : 'No questions match the current filters.'}
            </p>
          ) : (
            [...filteredQuestions].reverse().map(q => (
              <div key={q.id} className="bg-slate-700 p-4 rounded-md">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold uppercase text-indigo-400">
                      [{q.type === 'simple' ? 'SIMPLE' : 'STEM'}]
                    </span>

                    {q.type === 'simple' && getLevelBadge(q.level || 1)}
                    {q.type === 'simple' && q.marks > 1 && (
                      <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded flex items-center gap-1">
                        <Award size={10} />
                        {q.marks}
                      </span>
                    )}
                    {/* Display grades */}
                    {(q.grades?.length > 0 || q.grade) && (
                      <div className="flex gap-1">
                        {(Array.isArray(q.grades) ? q.grades : [q.grade]).filter(Boolean).map(g => (
                          <span key={g} className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">
                            {g}
                          </span>
                        ))}
                      </div>
                    )}
                    <span className={`px-1.5 py-0.5 text-xs rounded font-medium ${q.status === 'published'
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-amber-500/20 text-amber-400'
                      }`}>
                      {q.status === 'published' ? 'Published' : 'Draft'}
                    </span>
                    {q.hint && (
                      <div className="flex items-center gap-1">
                        {(q.hint.text || (typeof q.hint === 'string' && q.hint)) && (
                          <span className="text-yellow-400" title="Text hint">
                            <Type size={12} />
                          </span>
                        )}
                        {q.hint.audio && (
                          <span className="text-purple-400" title="Audio hint">
                            <Mic size={12} />
                          </span>
                        )}
                        {q.hint.video && (
                          <span className="text-red-400" title="Video hint">
                            <Video size={12} />
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleStartEdit(q)}
                      className="text-sm text-indigo-400 hover:text-indigo-300 font-semibold"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDuplicateQuestion(q)}
                      className="text-sm text-slate-300 hover:text-white font-semibold flex items-center gap-1"
                    >
                      <Layers size={12} />
                      Duplicate
                    </button>
                    <button
                      onClick={() => deleteQuestion(q.id)}
                      className="text-sm text-red-500 hover:text-red-400 font-semibold"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {q.type === 'dynamic' ? (
                  <div className="flex items-start gap-2">
                    <span className="px-2 py-0.5 bg-purple-500/30 text-purple-300 text-xs rounded flex items-center gap-1">
                      <Wand2 size={10} />
                      Dynamic
                    </span>
                    <div>
                      <p className="text-sm text-slate-200 font-mono">
                        {FUNCTION_TYPES.find(f => f.id === q.functionType)?.formula || q.functionType}
                      </p>
                      {q.sampleInstance?.equation && (
                        <p className="text-xs text-slate-400 mt-1">
                          Sample: {q.sampleInstance.equation}
                        </p>
                      )}
                    </div>
                  </div>
                ) : q.type === 'simple' ? (
                  <>
                    {/* MathRenderer for question text */}
                    <div className="font-semibold text-slate-200 prose prose-sm prose-invert max-w-none">
                      <MathRenderer content={q.text} enableMarkdown={true} enableMath={true} />
                    </div>
                    {/* Media display */}
                    {q.media && q.media.type !== 'none' && (
                      <QuestionMediaPreview media={q.media} />
                    )}
                    <ol className="mt-2 text-sm list-decimal list-inside">
                      {q.options.map(opt => (
                        <li
                          key={opt.id}
                          className={`${q.correctOptionIds.includes(opt.id) ? 'text-green-400 font-medium' : 'text-slate-400'}`}
                        >
                          <div className="prose prose-sm prose-invert max-w-none inline-block">
                            <MathRenderer content={opt.text} enableMarkdown={true} enableMath={true} />
                          </div>
                        </li>
                      ))}
                    </ol>
                  </>
                ) : (
                  <>
                    {/* MathRenderer for stem text */}
                    <div className="font-semibold text-slate-200 prose prose-sm prose-invert max-w-none">
                      <MathRenderer content={q.stemText} enableMarkdown={true} enableMath={true} />
                    </div>
                    {/* Media display */}
                    {q.media && q.media.type !== 'none' && (
                      <QuestionMediaPreview media={q.media} />
                    )}
                    <div className="mt-3 space-y-3 pl-4 border-l-2 border-slate-600">
                      {q.subQuestions.map((sq, idx) => (
                        <div key={sq.id} className="bg-slate-600/30 p-2 rounded">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs text-slate-500">{idx + 1}.</span>
                            {getLevelBadge(sq.level || 1)}
                            {sq.marks > 1 && (
                              <span className="px-1 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">
                                {sq.marks}m
                              </span>
                            )}
                            {sq.hint && (
                              <div className="flex items-center gap-0.5">
                                {sq.hint.text && <Type size={10} className="text-yellow-400" />}
                                {sq.hint.audio && <Mic size={10} className="text-purple-400" />}
                                {sq.hint.video && <Video size={10} className="text-red-400" />}
                              </div>
                            )}
                            {sq.subtopics?.length > 0 && (
                              <div className="flex gap-0.5">
                                {sq.subtopics.slice(0, 2).map(st => (
                                  <span key={st} className="px-1 py-0.5 bg-teal-500/20 text-teal-400 text-xs rounded">
                                    {st}
                                  </span>
                                ))}
                                {sq.subtopics.length > 2 && (
                                  <span className="text-xs text-slate-400">+{sq.subtopics.length - 2}</span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="text-sm font-medium text-slate-300 prose prose-sm prose-invert max-w-none">
                            <MathRenderer content={sq.text} enableMarkdown={true} enableMath={true} />
                          </div>
                          <ol className="mt-1 text-xs list-disc list-inside pl-4">
                            {(sq.options || []).map(opt => (
                              <li
                                key={opt.id}
                                className={`${sq.correctOptionIds.includes(opt.id) ? 'text-green-500' : 'text-slate-400'}`}
                              >
                                <div className="prose prose-xs prose-invert max-w-none inline-block">
                                  <MathRenderer content={opt.text} enableMarkdown={true} enableMath={true} />
                                </div>
                              </li>
                            ))}
                          </ol>
                          {/* Show knowledge for subquestion */}
                          {sq.requiredKnowledge && (sq.requiredKnowledge.skills?.length > 0 || sq.requiredKnowledge.concepts?.length > 0) && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {sq.requiredKnowledge.skills?.map((skill, i) => (
                                <span key={`skill-${i}`} className="px-1 py-0.5 bg-blue-600/20 text-blue-300 text-xs rounded">
                                  {skill}
                                </span>
                              ))}
                              {sq.requiredKnowledge.concepts?.map((concept, i) => (
                                <span key={`concept-${i}`} className="px-1 py-0.5 bg-purple-600/20 text-purple-300 text-xs rounded">
                                  {concept}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Show topics and subtopics */}
                {(q.topics?.length > 0 || q.subtopics?.length > 0) && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {q.topics?.map((topic, i) => (
                      <span key={`topic-${i}`} className="px-1.5 py-0.5 bg-green-600/20 text-green-300 text-xs rounded">
                        {topic}
                      </span>
                    ))}
                    {q.subtopics?.map((subtopic, i) => (
                      <span key={`subtopic-${i}`} className="px-1.5 py-0.5 bg-teal-600/20 text-teal-300 text-xs rounded">
                        {subtopic}
                      </span>
                    ))}
                  </div>
                )}

                {q.hint && q.type === 'simple' && (
                  <div className="mt-3 p-3 bg-yellow-900/20 border border-yellow-600/30 rounded-lg space-y-2">
                    <div className="flex items-center gap-2 text-xs text-yellow-400 font-medium">
                      <Lightbulb size={12} />
                      Hints
                    </div>
                    {(q.hint.text || (typeof q.hint === 'string' && q.hint)) && (
                      <div className="flex items-start gap-2 text-xs text-yellow-300">
                        <Type size={12} className="flex-shrink-0 mt-0.5" />
                        <MathRenderer
                          content={typeof q.hint === 'string' ? q.hint : q.hint.text}
                          enableMarkdown={true}
                          enableMath={true}
                        />
                      </div>
                    )}
                    {q.hint.audio?.url && (
                      <div className="flex items-center gap-2">
                        <Mic size={12} className="text-purple-400" />
                        <audio controls className="h-6 flex-1" src={q.hint.audio.url} />
                      </div>
                    )}
                    {q.hint.video && (
                      <div className="flex items-center gap-2">
                        {q.hint.video.type === 'youtube' ? (
                          <>
                            <Youtube size={12} className="text-red-400" />
                            <a
                              href={q.hint.video.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-400 hover:underline truncate"
                            >
                              {q.hint.video.url}
                            </a>
                          </>
                        ) : (
                          <>
                            <Video size={12} className="text-blue-400" />
                            <span className="text-xs text-slate-400">{q.hint.video.name}</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Required Knowledge for simple questions */}
                {q.type === 'simple' && q.requiredKnowledge && (q.requiredKnowledge.skills?.length > 0 || q.requiredKnowledge.concepts?.length > 0) && (
                  <div className="mt-2 p-2 bg-slate-600/30 rounded-lg">
                    <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
                      <BookOpen size={12} />
                      Required Knowledge
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {q.requiredKnowledge.skills?.map((skill, i) => (
                        <span key={`skill-${i}`} className="px-1.5 py-0.5 bg-blue-600/20 text-blue-300 text-xs rounded">
                          {skill}
                        </span>
                      ))}
                      {q.requiredKnowledge.concepts?.map((concept, i) => (
                        <span key={`concept-${i}`} className="px-1.5 py-0.5 bg-purple-600/20 text-purple-300 text-xs rounded">
                          {concept}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CreatorView;
